// src/components/ProveedorTable.jsx
import React, { useState, useMemo, useEffect } from "react";
import Paginador from "./Paginador";

const headerStyle = { cursor: "pointer", whiteSpace: "nowrap" };

// Helpers para ordenar IDs tipo P-/C-/A-#### (campo humano "id")
const getNumericPart = (id) => {
  const m = String(id || "")
    .toUpperCase()
    .match(/^[CPA]-(\d+)$/);
  return m ? parseInt(m[1], 10) : Number.POSITIVE_INFINITY;
};
const prefRank = (id) => {
  const c = String(id || "").toUpperCase()[0];
  // En tabla de proveedores priorizamos "P" por delante
  return c === "P" ? 0 : c === "C" ? 1 : c === "A" ? 2 : 3;
};

const ProveedorTable = ({
  proveedores = [],
  onEditProveedor,
  onDeleteProveedor,
}) => {
  // === Ordenamiento ===
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });

  const requestSort = (key) => {
    setSortConfig((prev) =>
      prev.key === key
        ? { key, direction: prev.direction === "asc" ? "desc" : "asc" }
        : { key, direction: "asc" }
    );
  };

  const sortedProveedores = useMemo(() => {
    if (!Array.isArray(proveedores)) return [];
    if (!sortConfig.key) return proveedores;

    const { key, direction } = sortConfig;
    const dir = direction === "asc" ? 1 : -1;

    return [...proveedores].sort((a, b) => {
      const va = a?.[key];
      const vb = b?.[key];

      // Orden especial para columna ID (humana)
      if (key === "id") {
        const ra = prefRank(va);
        const rb = prefRank(vb);
        if (ra !== rb) return (ra - rb) * dir;
        const na = getNumericPart(va);
        const nb = getNumericPart(vb);
        if (na === nb) return 0;
        return (na > nb ? 1 : -1) * dir;
      }

      // undefined/null al final
      if (va == null && vb == null) return 0;
      if (va == null) return 1;
      if (vb == null) return -1;

      // numérico si aplica
      const na =
        typeof va === "number" ? va : Number.isFinite(+va) ? +va : null;
      const nb =
        typeof vb === "number" ? vb : Number.isFinite(+vb) ? +vb : null;
      if (na != null && nb != null)
        return (na === nb ? 0 : na > nb ? 1 : -1) * dir;

      // alfabético
      const sa = String(va).toLocaleLowerCase();
      const sb = String(vb).toLocaleLowerCase();
      return sa.localeCompare(sb) * dir;
    });
  }, [proveedores, sortConfig]);

  const sortArrow = (key) =>
    sortConfig.key === key
      ? sortConfig.direction === "asc"
        ? " ▲"
        : " ▼"
      : "";

  // === Paginación ===
  const [itemsPerPage, setItemsPerPage] = useState(25);
  const [currentPage, setCurrentPage] = useState(1);

  const totalProveedores = sortedProveedores.length;
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;

  const paginatedProveedores = useMemo(
    () => sortedProveedores.slice(startIndex, endIndex),
    [sortedProveedores, startIndex, endIndex]
  );

  const handleItemsPerPageChange = (items) => setItemsPerPage(items);
  const handlePageChange = (page) => setCurrentPage(page);

  useEffect(() => {
    setCurrentPage(1);
  }, [proveedores, sortConfig, itemsPerPage]);

  return (
    <div className="tabla-cotizaciones-wrapper">
      <div className="tabla-cotizaciones-content">
        <table>
          <thead>
            <tr>
              <th style={headerStyle} onClick={() => requestSort("id")}>
                ID{sortArrow("id")}
              </th>
              <th style={headerStyle} onClick={() => requestSort("empresa")}>
                Nombre{sortArrow("empresa")}
              </th>
              <th style={headerStyle} onClick={() => requestSort("rfc")}>
                RFC{sortArrow("rfc")}
              </th>
              <th style={headerStyle} onClick={() => requestSort("telefono")}>
                Teléfono{sortArrow("telefono")}
              </th>
              <th style={headerStyle} onClick={() => requestSort("correo")}>
                Correo Electrónico{sortArrow("correo")}
              </th>
              <th
                style={headerStyle}
                onClick={() => requestSort("cuentasPagar")}
              >
                Cuentas por Pagar{sortArrow("cuentasPagar")}
              </th>
              <th>Acciones</th>
            </tr>
          </thead>

          <tbody>
            {paginatedProveedores.map((proveedor) => {
              const isProveedor =
                (proveedor?.tipo ?? "Proveedor") === "Proveedor";
              return (
                <tr key={proveedor.__docId}>
                  {/* Mostrar ID humano solo si es tipo Proveedor */}
                  <td style={{ width: "10%" }}>
                    {isProveedor ? proveedor.id || "-" : ""}
                  </td>
                  <td style={{ width: "30%" }}>
                    {proveedor.empresa || "Sin nombre"}
                  </td>
                  <td>{proveedor.rfc || "Sin RFC"}</td>
                  <td>{proveedor.telefono || "Sin teléfono"}</td>
                  <td>{proveedor.correo || "Sin correo"}</td>
                  <td>{proveedor.cuentasPagar ?? "Sin datos"}</td>
                  <td
                    style={{
                      width: 80,
                      textAlign: "center",
                      padding: "4px 2px",
                      whiteSpace: "nowrap",
                    }}
                  >
                    <i
                      className="bi bi-pencil-fill"
                      style={{ cursor: "pointer", marginRight: 8 }}
                      title="Editar proveedor"
                      onClick={() => onEditProveedor?.(proveedor.__docId)}
                    />
                    <i
                      className="bi bi-trash-fill"
                      style={{ cursor: "pointer", color: "#dc3545" }}
                      title="Eliminar proveedor"
                      onClick={() => onDeleteProveedor?.(proveedor.__docId)}
                    />
                  </td>
                </tr>
              );
            })}

            {totalProveedores === 0 && (
              <tr>
                <td colSpan={7} style={{ textAlign: "center", padding: 12 }}>
                  No hay proveedores para mostrar.
                </td>
              </tr>
            )}
          </tbody>
        </table>

        {totalProveedores > 0 && paginatedProveedores.length === 0 && (
          <div className="no-resultados" style={{ padding: 12 }}>
            No hay proveedores en esta página.
          </div>
        )}
      </div>

      <div className="tabla-cotizaciones-footer">
        <div className="paginador-container">
          <Paginador
            totalItems={totalProveedores}
            itemsPerPage={itemsPerPage}
            currentPage={currentPage}
            onPageChange={handlePageChange}
            onItemsPerPageChange={handleItemsPerPageChange}
          />
        </div>
      </div>
    </div>
  );
};

export default ProveedorTable;
